import { useState, useEffect } from "react";
import { casualData } from "../api/casualData.js";
import { ApiError } from "../api/client.js";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("lt-LT"); } catch { return "—"; }
}

// Vienos master paskyros eilutė — peržiūra arba redagavimo forma (el. paštas +
// naujas slaptažodis, abu nebūtini — tuščias slaptažodžio laukas jo nekeičia).
function AccountRow({ account, isSelf, onSave, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState(account.email);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function startEdit() {
    setEmail(account.email);
    setPassword("");
    setError(null);
    setEditing(true);
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const patch = {};
      if (email.trim() && email.trim().toLowerCase() !== account.email) patch.email = email.trim();
      if (password.trim()) patch.password = password.trim();
      if (Object.keys(patch).length > 0) await onSave(account.id, patch);
      setEditing(false);
    } catch (e) {
      setError(e instanceof ApiError ? (e.body?.message || "Nepavyko išsaugoti.") : "Nepavyko išsaugoti.");
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, marginBottom: 8 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 3 }}>El. paštas</div>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 8px", fontSize: 13, boxSizing: "border-box" }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 3 }}>Naujas slaptažodis (nebūtina)</div>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Palikite tuščią, jei nekeičiate" minLength={10}
              style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 8px", fontSize: 13, boxSizing: "border-box" }} />
          </div>
        </div>
        {error && <div style={{ fontSize: 12, color: "#dc2626", marginBottom: 8 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8 }}>
          <button disabled={saving} onClick={save}
            style={{ background: "#383853", color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
            {saving ? "Saugoma..." : "💾 Išsaugoti"}
          </button>
          <button onClick={() => setEditing(false)}
            style={{ background: "#f3f4f6", color: "#383853", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer" }}>
            Atšaukti
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 14px", marginBottom: 8 }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#1c1c1c" }}>
          {account.email}{isSelf && <span style={{ fontSize: 11, color: "#735DFF", fontWeight: 700, marginLeft: 6 }}>(jūs)</span>}
        </div>
        <div style={{ fontSize: 11, color: "#9ca3af" }}>
          {account.emailVerified ? "El. paštas patvirtintas" : "El. paštas nepatvirtintas"} · sukurta {formatDate(account.createdAt)}
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button onClick={startEdit} title="Redaguoti"
          style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 14 }}>✏</button>
        <button
          onClick={() => { if (window.confirm(`Pašalinti master teises iš ${account.email}?`)) onRemove(account.id); }}
          disabled={isSelf}
          title={isSelf ? "Negalite pašalinti savo paskyros" : "Pašalinti master teises"}
          style={{ background: "none", border: "none", color: isSelf ? "#e5e7eb" : "#d1d5db", cursor: isSelf ? "default" : "pointer", fontSize: 16 }}>
          ×
        </button>
      </div>
    </div>
  );
}

export function AdminAccountsView({ user }) {
  const [accounts, setAccounts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  function load() {
    casualData.getAdminAccounts()
      .then(data => setAccounts(Array.isArray(data) ? data : []))
      .catch(e => console.error("Failed to load admin accounts", e))
      .finally(() => setLoaded(true));
  }

  useEffect(() => { load(); }, []);

  async function submitAdd() {
    const email = newEmail.trim();
    if (!email) return;
    setAdding(true);
    setAddError(null);
    try {
      await casualData.createAdminAccount(email);
      setNewEmail("");
      setShowAdd(false);
      load();
    } catch (e) {
      setAddError(e instanceof ApiError ? (e.body?.message || "Nepavyko sukurti paskyros.") : "Nepavyko sukurti paskyros.");
    } finally {
      setAdding(false);
    }
  }

  async function saveAccount(id, patch) {
    await casualData.updateAdminAccount(id, patch);
    load();
  }

  async function removeAccount(id) {
    try {
      await casualData.removeAdminAccount(id);
      load();
    } catch (e) {
      alert(e instanceof ApiError ? (e.body?.message || "Nepavyko pašalinti.") : "Nepavyko pašalinti.");
    }
  }

  if (!loaded) return <p style={{ fontSize: 14, color: "#383853" }}>Kraunama...</p>;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: "#1C1C1C" }}>Master paskyros</h1>
        {!showAdd && (
          <button onClick={() => { setShowAdd(true); setNewEmail(""); setAddError(null); }}
            style={{ background: "#735DFF", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            + Naujas master
          </button>
        )}
      </div>

      <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>
        Nauja paskyra sukuriama (arba esama paaukštinama) ir jai iškart išsiunčiamas slaptažodžio nustatymo el. laiškas —
        slaptažodį čia taip pat galima nustatyti tiesiogiai, redaguojant paskyrą.
      </p>

      {showAdd && (
        <div style={{ border: "1px solid #fecaca", background: "#fff8f6", borderRadius: 8, padding: 10, marginBottom: 16, display: "flex", gap: 8, alignItems: "center" }}>
          <input autoFocus type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") submitAdd(); if (e.key === "Escape") setShowAdd(false); }}
            placeholder="el.pastas@pavyzdys.lt"
            style={{ flex: 1, border: "1px solid #e5e7eb", borderRadius: 6, padding: "7px 10px", fontSize: 13, boxSizing: "border-box" }} />
          <button disabled={adding} onClick={submitAdd}
            style={{ background: "#735DFF", color: "#fff", border: "none", borderRadius: 6, padding: "7px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: adding ? 0.6 : 1 }}>
            {adding ? "Kuriama..." : "Sukurti"}
          </button>
          <button onClick={() => setShowAdd(false)} style={{ background: "#f3f4f6", color: "#383853", border: "none", borderRadius: 6, padding: "7px 12px", fontSize: 13, cursor: "pointer" }}>
            Atšaukti
          </button>
        </div>
      )}
      {addError && <div style={{ fontSize: 12, color: "#dc2626", marginBottom: 12 }}>{addError}</div>}

      {accounts.length === 0 ? (
        <p style={{ fontSize: 13, color: "#9ca3af" }}>Master paskyrų nėra.</p>
      ) : (
        accounts.map(acc => (
          <AccountRow key={acc.id} account={acc} isSelf={acc.id === user?.id} onSave={saveAccount} onRemove={removeAccount} />
        ))
      )}
    </div>
  );
}
